import React, { useState, useEffect } from "react";

// Icons
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

const PasswordRequirementsCard = ({
  password,
  passwordConfirm,
  setPasswordWarning,
  setPasswordConfirmWarning,
  lowercasePresent,
  setLowercasePresent,
  uppercasePresent,
  setUppercasePresent,
  numberPresent,
  setNumberPresent,
  specialPresent,
  setSpecialPresent,
  lengthPresent,
  setLengthPresent,
  matchingPresent,
  setMatchingPresent,
  takeFullWidth = false,
}) => {
  const [cardColor, setCardColor] = useState("bg-orange-200");

  const StandardRow = ({ standardTitle, standardCondition }) => {
    return (
      <div className="flex flex-grow items-center justify-between">
        <div className="flex-grow-9 flex items-center">{standardTitle}</div>
        <div className="flex-grow-1 flex items-center justify-center">
          {standardCondition ? (
            <CheckCircleIcon className="h-7 w-7 flex-none text-neutral-950" />
          ) : (
            <XCircleIcon className="h-7 w-7 flex-none text-neutral-950" />
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    // Check if password meets requirements.

    if (/[a-z]/.test(password)) {
      setLowercasePresent(true);
    } else {
      setLowercasePresent(false);
    }

    if (/[A-Z]/.test(password)) {
      setUppercasePresent(true);
    } else {
      setUppercasePresent(false);
    }

    if (/\d/.test(password)) {
      setNumberPresent(true);
    } else {
      setNumberPresent(false);
    }

    if (/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password)) {
      setSpecialPresent(true);
    } else {
      setSpecialPresent(false);
    }

    if (password.length >= 12) {
      setLengthPresent(true);
    } else {
      setLengthPresent(false);
    }

    if (password === passwordConfirm) {
      setMatchingPresent(true);
    } else {
      setMatchingPresent(false);
    }
  }, [
    password,
    passwordConfirm,
    setLowercasePresent,
    setUppercasePresent,
    setNumberPresent,
    setSpecialPresent,
    setLengthPresent,
    setMatchingPresent,
  ]);

  useEffect(() => {
    // Change card color if password meets all requirements.
    if (
      lowercasePresent &&
      uppercasePresent &&
      numberPresent &&
      specialPresent &&
      lengthPresent &&
      matchingPresent
    ) {
      setCardColor("bg-green-100");
      setPasswordWarning(false);
      setPasswordConfirmWarning(false);
    } else {
      setCardColor("bg-orange-200");
    }
  }, [
    lowercasePresent,
    uppercasePresent,
    numberPresent,
    specialPresent,
    lengthPresent,
    matchingPresent,
  ]);

  return (
    <div
      className={
        `${cardColor} flex flex-col p-3 rounded-xl Xshadow-sm transition
    mt-5
    ` + (takeFullWidth ? "w-full" : "w-full sm:w-4/5")
      }
    >
      <div className="flex">
        <div className="text-lg font-semibold mb-2">Password Requirements</div>
      </div>

      <StandardRow
        standardTitle={<i>Lowercase (a, b, c, ..., z) </i>}
        standardCondition={lowercasePresent}
      />

      <StandardRow
        standardTitle={<i>Uppercase (A, B, C, ..., Z) </i>}
        standardCondition={uppercasePresent}
      />

      <StandardRow
        standardTitle={<i>Number (1, 2, 3, ...) </i>}
        standardCondition={numberPresent}
      />

      <StandardRow
        standardTitle={<i>Special Character (!, @, #, ..., $) </i>}
        standardCondition={specialPresent}
      />

      <StandardRow
        standardTitle={<i>Length (Password Length ≥ 12) </i>}
        standardCondition={lengthPresent}
      />

      <StandardRow
        standardTitle={<i>Confirmation (Password Match) </i>}
        standardCondition={matchingPresent}
      />
    </div>
  );
};

export default PasswordRequirementsCard;
